import React from 'react';
import { useTranslation } from 'react-i18next';

import { TOC_STATUS } from '../../../types/courseToc.constants';
import type { TocStatus } from '../../../types/courseToc.constants';

/**
 * PracticeStatusChip - progress badge for the custom practice flow (TANAI-1869).
 *
 * Same data (`TocStatus`) as the StudentSkillsProgress ToC, but its OWN visual:
 * a friendly emoji + label per status. The 5 statuses collapse to 4 buckets
 * (IN_PRACTICE and IN_REVIEW share the "in progress" chip).
 */

type PracticeStatusChipProps = {
  status: TocStatus;
};

type ChipVisual = {
  emoji: string;
  labelKey: string;
  modifier: string;
};

const STATUS_VISUAL: Record<TocStatus, ChipVisual> = {
  [TOC_STATUS.MASTERED]: { emoji: '🎯', labelKey: 'practice_more_status_mastered', modifier: 'mastered' },
  [TOC_STATUS.IN_PRACTICE]: { emoji: '💪', labelKey: 'practice_more_status_in_progress', modifier: 'in-progress' },
  [TOC_STATUS.IN_REVIEW]: { emoji: '💪', labelKey: 'practice_more_status_in_progress', modifier: 'in-progress' },
  [TOC_STATUS.NOT_STARTED]: { emoji: '🆕', labelKey: 'practice_more_status_not_started', modifier: 'not-started' },
  [TOC_STATUS.BLOCKED]: { emoji: '🫠', labelKey: 'practice_more_status_blocked', modifier: 'blocked' },
};

function PracticeStatusChip({ status }: Readonly<PracticeStatusChipProps>): JSX.Element {
  const { t } = useTranslation(['chatbot']);
  // Guard against an unexpected status from the backend (the read-model does not
  // normalise unit/lesson status), so the chip never crashes on a missing entry.
  const visual = STATUS_VISUAL[status] ?? STATUS_VISUAL[TOC_STATUS.NOT_STARTED];

  return (
    <span className={`practice-status-chip practice-status-chip--${visual.modifier}`}>
      <span aria-hidden="true" className="practice-status-chip__emoji">
        {visual.emoji}
      </span>
      {t(visual.labelKey)}
    </span>
  );
}

export default PracticeStatusChip;
